// src/components/Map/MarkerManager/Marker.tsx

import React, { memo, useCallback, useMemo } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { ROUTES } from "../../../config/routes";
import { Marker, Tooltip } from "react-leaflet";
import { Icon } from "leaflet";
import MapIconPin from "../../../assets/icons/MapIconPin.svg";
import MapIconPinWarning from "../../../assets/icons/MapIconPinWarning.svg";
import ArchiveBoxIcon from "@heroicons/react/24/outline/ArchiveBoxIcon";
import { ReactComponent as MosquitoIconSvg } from "../../../assets/icons/MosquitoVector.svg";

import { Typography, Badge } from "antd";
import { MarkerData } from "../../../types/components/map";

const { Text } = Typography;

const makeTrapIcon = (warning: boolean, clickable: boolean) =>
  new Icon({
    iconUrl: warning ? MapIconPinWarning : MapIconPin,
    iconAnchor: [15, 40],
    className: `w-10 h-10 translate-y-[20px] z-1000 ${clickable ? "" : " !cursor-grab"}`,
  });

const TrapMarker = memo(({ marker }: { marker: MarkerData }) => {
  const navigate = useNavigate();

  const isClickable = marker.markerType !== "device";

  const icon = useMemo(
    () => makeTrapIcon(!!marker.warning, isClickable),
    [marker.warning, isClickable],
  );

  const handleNavigate = useCallback(() => {
    if (marker?.id) {
      navigate(generatePath(ROUTES.location_detail, { id: marker.id }));
    }
  }, [marker?.id, navigate]);

  return (
    <Marker
      position={marker.position}
      zIndexOffset={700}
      icon={icon}
      eventHandlers={isClickable ? { click: handleNavigate } : undefined}
    >
      <Tooltip
        key={`${marker?.warning}`}
        permanent
        className={`map-marker ${marker?.warning ? "warning" : ""}`}
        direction={"auto"}
      >
        <div
          className="px-2 py-2 flex items-center min-w-[12rem] gap-3 pr-6"
          style={isClickable ? { cursor: "pointer" } : undefined}
          onClick={isClickable ? handleNavigate : undefined}
        >
          <div className="flex flex-col flex-grow justify-center">
            <div className="flex items-center gap-2">
              {marker.markerType === "device" && (
                <ArchiveBoxIcon
                  className="w-5 h-5 flex-shrink-0"
                  style={{ color: "#0c4a6e" }}
                />
              )}
              <Text
                className="marker-display-name m-0"
                style={{
                  color: "#0c4a6e",
                  fontSize: "1.2rem",
                  lineHeight: "1.2",
                }}
              >
                {marker.displayName}
              </Text>
            </div>
            {marker.markerType === "device" && marker.gps && (
              <Text
                className="marker-gps-text m-0 mt-1 pl-7"
                style={{
                  color: "#0c4a6e",
                  fontSize: "0.9rem",
                  lineHeight: "1.2",
                }}
              >
                {marker.gps.lat.toFixed(4)}, {marker.gps.lon.toFixed(4)}
              </Text>
            )}
          </div>

          <div className="flex items-center gap-2">
            {marker.maintainCount ? (
              <Badge
                count={marker.maintainCount}
                offset={[0, 2]}
                color="#f4c000"
              >
                <ArchiveBoxIcon
                  className="w-5 h-5 trapIcon warning"
                  style={{ color: "#f4c000" }}
                />
              </Badge>
            ) : null}
            {marker.detectionsCount ? (
              <Badge
                count={marker.detectionsCount}
                color="#f4c000"
                offset={[0, 2]}
              >
                <MosquitoIconSvg
                  width={20}
                  height={20}
                  className="trapIcon"
                  fill="#f4c000"
                />
              </Badge>
            ) : null}
          </div>
        </div>
      </Tooltip>
    </Marker>
  );
});

export default TrapMarker;
